import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import i18n from 'i18next';
import { takeToolLockSnapshot, updateToolConfig } from '@/api/toolConfig.api';
import {
  dropToolOutputs,
  placedToolOutputs,
  toolOutputDropUndo,
  toolOutputRestoreUndo,
  type PlacedToolOutput,
} from '@/components/Nodes/toolOutputDelete';
import {
  StorybookFlowContext,
  useEdges,
  useFlowView,
  useGetNodes,
  useLockedNodeIdsKey,
} from '@/components/Recipe/FlowContext';
import { UndoRedoEntry } from '@/components/Recipe/undoRedo';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { queryClient } from '@/providers/queryClient';
import { useGlobalStore } from '@/state/global.state';
import { isEditingBlocked, useWorkflowStore, type WorkflowState } from '@/state/workflow.state';
import { EMPTY_TOOL_CONFIG } from '@/types/tool-config.types';
import { sanitizeError } from '@/utils/sanitize-error';
import { TOOL_TAB_EVENT_NAMES } from '@/consts/analytics.consts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import {
  overlayToolConfig,
  reorderToolConfigInputs,
  reorderToolConfigOutputs,
  resolveToolConfig,
  TOOL_OUTPUT_HANDLE_TYPES,
  toToolConfig,
  toWorkflowApp,
} from './toolConfigModel';
import {
  applyCoreToolConfigChange,
  clearWriteState,
  confirmedConfigs,
  optimisticConfigs,
  ownStoreConfigs,
  queueToolConfigWrite,
  withCoreChanges,
  writeGenerations,
  writesInFlight,
} from './toolConfigWriteQueue';
import { closeToolLockSnapshotTake, openToolLockSnapshotTake, releaseFromToolLockSnapshot } from './toolLockSnapshot';
import type {
  OutputExampleState,
  ToolConfig,
  ToolConfigInput,
  ToolConfigMetadata,
  ToolConfigOutput,
  ToolConfigWithInputs,
} from '@/types/tool-config.types';
import type { WorkflowApp } from '@/types/workflow-app.types';
import type { NodeId } from 'web';

const logger = log.getLogger('useWorkflowApp');

/**
 * The tool's I/O for this workflow, read off the version's `toolConfig`, which arrives with the
 * graph — there is no second request. The inputs stay derived from the outputs; the config supplies
 * only the overrides: `exposed`, order, description, and `defaultValue`.
 */
export function useWorkflowAppByWorkflowIdQuery(workflowId: string | undefined) {
  const recipeId = useWorkflowStore((state) => state.recipe.id);
  const toolConfig = useWorkflowStore((state) => state.recipe.toolConfig);
  // Read only while there is no config: the old Design App tab's input settings, for the import.
  const designAppMetadata = useWorkflowStore((state) => state.recipe.designAppMetadata);
  const getNodes = useGetNodes();
  // Subscribed to the edges, not the nodes: a node can only enter or leave the input set by being
  // wired or unwired, and `nodes` changes on every drag frame while `edges` does not.
  const edges = useEdges();
  const flowView = useFlowView();
  // The import declares a model in place of its legacy Output only while the model is unlocked.
  const lockedNodeIdsKey = useLockedNodeIdsKey();

  const data = useMemo<WorkflowApp | null>(() => {
    if (!workflowId || workflowId !== recipeId) {
      return null;
    }
    // `getNodes` doesn't rerun this when a lock flips; the key does, and the import reads the locks.
    void lockedNodeIdsKey;
    const graph = { nodes: getNodes(), edges };
    const config = resolveToolConfig(toolConfig, graph, {
      inputSettings: designAppMetadata,
      isOutputHandle: (nodeId, handleId) => {
        const type = flowView.nodeOutputType(nodeId, handleId);
        return type !== undefined && TOOL_OUTPUT_HANDLE_TYPES.includes(type);
      },
    });
    return {
      ...toWorkflowApp(workflowId, config, graph),
      // Read at write time: this memo follows the edges, so its node list can miss a delete or an undo.
      getCanvasNodeIds: () => new Set(getNodes().map((node) => node.id)),
    };
  }, [designAppMetadata, edges, flowView, getNodes, lockedNodeIdsKey, recipeId, toolConfig, workflowId]);

  return { data, isLoading: false };
}

/**
 * @deprecated There is nothing to create: a config is born with the first write. Kept so the panel
 * compiles until it reads `ToolConfig` directly (WEA-7015).
 */
const NO_CREATE_MUTATION = {
  mutate: (_variables?: { workflowId: string }) => {},
  data: null as WorkflowApp | null,
  isPending: false,
  isError: false,
} as const;

export function useCreateWorkflowAppMutation() {
  return NO_CREATE_MUTATION;
}

/** The same conditions `saveRecipe` refuses on: a snapshot preview, a non-editor, a blocked editor. */
function canWriteToolConfig(
  state: Pick<WorkflowState, 'previewSnapshotId' | 'workflowRole' | 'workflowError'>,
): boolean {
  return state.previewSnapshotId === null && state.workflowRole === 'editor' && !isEditingBlocked(state.workflowError);
}

/**
 * One writer for both lists, because the route takes the whole config. The store is updated first
 * and rolled back if the write fails, so the canvas and the drawer never sit on a state the server
 * rejected.
 *
 * Module-level so a node delete and an undo can join the same queue as the panel without each
 * canvas node subscribing to the config.
 */
async function commitToolConfig(
  recipeId: string,
  base: ToolConfigWithInputs,
  update: (current: ToolConfigWithInputs) => ToolConfigWithInputs,
  onBegin?: () => void,
  graphNodeIds?: ReadonlySet<string>,
  onApplied?: () => void,
): Promise<boolean> {
  if (!canWriteToolConfig(useWorkflowStore.getState())) {
    return true;
  }
  const { recipe, setRecipe } = useWorkflowStore.getState();
  if (recipe.id !== recipeId) {
    return true;
  }
  // Drawer `base` has graph-derived rows; overlay has outputs and saved field overrides.
  const current = overlayToolConfig(base, optimisticConfigs.get(recipeId) ?? recipe.toolConfig, graphNodeIds);
  const next = update(current);
  if (next === current) {
    return true;
  }
  const inFlight = writesInFlight.get(recipeId) ?? 0;
  if (inFlight === 0) {
    confirmedConfigs.set(recipeId, recipe.toolConfig ?? null);
  }
  optimisticConfigs.set(recipeId, next);
  writesInFlight.set(recipeId, inFlight + 1);
  const generation = (writeGenerations.get(recipeId) ?? 0) + 1;
  writeGenerations.set(recipeId, generation);
  onBegin?.();

  setRecipe({ ...recipe, toolConfig: next });
  ownStoreConfigs.set(recipeId, next);
  try {
    // After the draft is visible, still before the request. Callers record undo here so Cmd+Z works
    // during the write and a late `flowGraph.edit` cannot cancel a drag that started since.
    onApplied?.();
    await queueToolConfigWrite(() => updateToolConfig(recipeId, next));
    confirmedConfigs.set(recipeId, withCoreChanges(recipeId, next));
    // Keyed on `recipes.updatedAt`, which a config-only write does not move, so "update available"
    // would otherwise wait for the next graph save.
    void queryClient.invalidateQueries({ queryKey: ['recipe-current-content', recipeId] });
  } catch (error) {
    // Undo only if nothing newer replaced this write. When something did, that later write carries
    // this one's change too, and rolls back to the confirmed config itself if it also fails. Nor over
    // a config a reload put in the store since.
    const state = useWorkflowStore.getState();
    if (
      state.recipe.id === recipeId &&
      writeGenerations.get(recipeId) === generation &&
      state.recipe.toolConfig === ownStoreConfigs.get(recipeId)
    ) {
      state.setRecipe({ ...state.recipe, toolConfig: confirmedConfigs.get(recipeId) ?? null });
    }
    logger.error('Tool config write failed; rolled back', sanitizeError(error));
    useGlobalStore.getState().updateSnackbarData({
      text: i18n.t(I18N_KEYS.RECIPE_MAIN.ERRORS.SAVE_ERRORS.GENERAL.MESSAGE),
      isOpen: true,
      icon: null,
      severity: 'error',
    });
    return false;
  } finally {
    const remaining = (writesInFlight.get(recipeId) ?? 1) - 1;
    if (remaining === 0) {
      clearWriteState(recipeId);
    } else {
      writesInFlight.set(recipeId, remaining);
    }
  }
  return true;
}

/** A stored row can omit either list. `overlayToolConfig` maps `base.inputs` and expects both. */
function storedToolConfigBase(stored: ToolConfig | null | undefined): ToolConfigWithInputs {
  if (!stored) {
    return EMPTY_TOOL_CONFIG;
  }
  return {
    ...stored,
    outputs: Array.isArray(stored.outputs) ? stored.outputs : [],
    inputs: Array.isArray(stored.inputs) ? stored.inputs : [],
  };
}

/**
 * Replaces the draft's output list in one PATCH. `update` must return the same array when nothing
 * changed. Published versions are a different row and are not written.
 */
export async function applyDraftToolOutputs(
  recipeId: string,
  update: (outputs: ToolConfigOutput[]) => ToolConfigOutput[],
  updateInputs?: (inputs: ToolConfigInput[]) => ToolConfigInput[],
): Promise<boolean> {
  const base = storedToolConfigBase(useWorkflowStore.getState().recipe.toolConfig);
  return commitToolConfig(recipeId, base, (current) => {
    const outputs = update(current.outputs);
    const inputs = updateInputs ? updateInputs(current.inputs) : current.inputs;
    if (outputs === current.outputs && inputs === current.inputs) {
      return current;
    }
    return { ...current, outputs, inputs };
  });
}

function useToolConfigWriter(workflowApp: WorkflowApp | null | undefined) {
  const [isSaving, setIsSaving] = useState(false);
  const localWritesInFlight = useRef(0);
  const getCanvasNodeIds = workflowApp?.getCanvasNodeIds;

  const save = useCallback(
    async (
      recipeId: string,
      base: ToolConfigWithInputs,
      update: (current: ToolConfigWithInputs) => ToolConfigWithInputs,
      onApplied?: () => void,
    ) => {
      let began = false;
      try {
        return await commitToolConfig(
          recipeId,
          base,
          update,
          () => {
            began = true;
            localWritesInFlight.current += 1;
            setIsSaving(true);
          },
          getCanvasNodeIds?.(),
          onApplied,
        );
      } finally {
        if (began) {
          localWritesInFlight.current -= 1;
          if (localWritesInFlight.current === 0) {
            setIsSaving(false);
          }
        }
      }
    },
    [getCanvasNodeIds],
  );

  return { save, isSaving };
}

/**
 * How long an open workflow waits before asking for the lock snapshot, so a workflow the next store
 * update replaces (a quick switch to another one) never takes one.
 */
const LOCK_SNAPSHOT_DELAY_MS = 300;

/**
 * Has core take the lock snapshot the first time an editor opens the workflow with the Tool tab flag on.
 * Locks made before then (with the flag off) keep hiding their inputs; from then on a lock no longer
 * changes a tool input's exposure; see `isKeptOutByLock`. Writes only that field, server-side, so it
 * can't overwrite anything a stale tab holds. On a published tool this leaves a draft change that the
 * update banner doesn't count (see `workflowVersionContent`).
 */
export function useToolLockSnapshot(recipeId: string | undefined) {
  const hasSnapshot = useWorkflowStore((state) => Array.isArray(state.recipe.toolConfig?.lockedAtConversion));
  // Subscribed, because the role can settle after the workflow opens: the Figma embed boots read-only and
  // becomes an editor in place.
  const canWrite = useWorkflowStore(canWriteToolConfig);
  /** Once per workflow per mount, so a failure (or a published latest version) isn't retried in a loop. */
  const requested = useRef<string | null>(null);

  useEffect(() => {
    if (!recipeId || hasSnapshot || requested.current === recipeId || !canWrite) {
      return;
    }
    // From here, not from the request: an unlock during the delay is just as recent as the flag.
    openToolLockSnapshotTake(recipeId);
    let sent = false;
    const timer = setTimeout(() => {
      sent = true;
      requested.current = recipeId;
      takeToolLockSnapshot(recipeId)
        .then(({ lockedAtConversion }) => {
          if (lockedAtConversion) {
            // A workflow with no config gets one holding only the snapshot, which still declares no tool.
            applyCoreToolConfigChange(recipeId, (config) => ({ ...config, lockedAtConversion }) as ToolConfig);
          }
          closeToolLockSnapshotTake(recipeId, lockedAtConversion);
        })
        .catch((error: unknown) => {
          closeToolLockSnapshotTake(recipeId, null);
          logger.warn('Tool lock snapshot failed', sanitizeError(error));
        });
    }, LOCK_SNAPSHOT_DELAY_MS);
    return () => {
      clearTimeout(timer);
      if (!sent) {
        closeToolLockSnapshotTake(recipeId, null);
      }
    };
  }, [canWrite, hasSnapshot, recipeId]);
}

function toolConfigListsOutput(nodeId: NodeId): boolean {
  return (useWorkflowStore.getState().recipe.toolConfig?.outputs ?? []).some((output) => output.nodeId === nodeId);
}

function sameEntries<T>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((entry, index) => entry === right[index]);
}

export function useAppOutputMutations(workflowApp: WorkflowApp | null | undefined) {
  const { save, isSaving } = useToolConfigWriter(workflowApp);
  const { track } = useAnalytics();
  // Optional: Tool-tab mutations still work without a graph (unit tests); undo joins the editor
  // stack only when the recipe FlowContext is present — same pattern as file rename.
  const flowGraph = useContext(StorybookFlowContext)?.graph;

  const setAsOutput = useCallback(
    (nodeId: NodeId) => {
      if (!workflowApp) {
        return;
      }
      const recipeId = workflowApp.workflowId;
      const base = toToolConfig(workflowApp.draftOverrides);
      const inputNodeIds = new Set(base.inputs.map((input) => input.nodeId));
      let placed: PlacedToolOutput[] = [];
      let removedInputs: ToolConfigInput[] = [];
      let undoEntry: UndoRedoEntry | undefined;
      void save(
        recipeId,
        base,
        (current) => {
          if (current.outputs.some((output) => output.nodeId === nodeId)) {
            return current;
          }
          // A current graph-derived input cannot also be an output. Do not consult the overlaid stored
          // list: it deliberately retains a stale row after an output is removed so its settings survive
          // rewiring, and that row must not prevent the output from being restored.
          if (inputNodeIds.has(nodeId)) {
            return current;
          }
          track(
            TOOL_TAB_EVENT_NAMES.OUTPUT_CHANGED,
            {
              action: 'added',
              node_id: nodeId,
              workflow_id: recipeId,
              tool_id: recipeId,
            },
            TrackTypeEnum.BI,
          );
          placed = [{ output: { nodeId }, index: current.outputs.length }];
          removedInputs = current.inputs.filter((input) => input.nodeId === nodeId).map((input) => ({ ...input }));
          return {
            ...current,
            inputs: current.inputs.filter((input) => input.nodeId !== nodeId),
            outputs: [...current.outputs, { nodeId }],
          };
        },
        () => {
          if (!flowGraph || placed.length === 0) {
            return;
          }
          const entry = toolOutputDropUndo(recipeId, placed, removedInputs);
          undoEntry = entry;
          flowGraph.edit(() => entry);
        },
      ).then((ok) => {
        if (ok || !flowGraph || !undoEntry) {
          return;
        }
        // A later write can reject this request without restoring the draft, and it keeps this add.
        // Drop the step only when the output is actually gone, including when a canvas edit buried it.
        if (!toolConfigListsOutput(nodeId)) {
          flowGraph.discardUndoEntry(undoEntry);
        }
      });
    },
    [flowGraph, save, track, workflowApp],
  );

  const removeOutput = useCallback(
    (nodeId: NodeId) => {
      if (!workflowApp) {
        return;
      }
      const recipeId = workflowApp.workflowId;
      const base = toToolConfig(workflowApp.draftOverrides);
      let placed: PlacedToolOutput[] = [];
      let undoEntry: UndoRedoEntry | undefined;
      void save(
        recipeId,
        base,
        (current) => {
          const removed = current.outputs.filter((output) => output.nodeId === nodeId);
          if (removed.length === 0) {
            return current;
          }
          track(
            TOOL_TAB_EVENT_NAMES.OUTPUT_CHANGED,
            {
              action: 'removed',
              node_id: nodeId,
              workflow_id: recipeId,
              tool_id: recipeId,
            },
            TrackTypeEnum.BI,
          );
          placed = placedToolOutputs(current.outputs, removed);
          return { ...current, outputs: dropToolOutputs(current.outputs, placed) };
        },
        () => {
          if (!flowGraph || placed.length === 0) {
            return;
          }
          const entry = toolOutputRestoreUndo(recipeId, placed);
          undoEntry = entry;
          flowGraph.edit(() => entry);
        },
      ).then((ok) => {
        if (ok || !flowGraph || !undoEntry) {
          return;
        }
        // Same as add: a failed remove only loses its undo when the output is still on the draft.
        if (toolConfigListsOutput(nodeId)) {
          flowGraph.discardUndoEntry(undoEntry);
        }
      });
    },
    [flowGraph, save, track, workflowApp],
  );

  /** Edits an output's picked `examples` and its `uploads` in one write, so an upload or a delete lands whole. */
  const setOutputExamples = useCallback(
    (nodeId: NodeId, update: (state: OutputExampleState) => OutputExampleState) => {
      if (!workflowApp) {
        return Promise.resolve(true);
      }
      const base = toToolConfig(workflowApp.draftOverrides);
      return save(workflowApp.workflowId, base, (current) => {
        let changed = false;
        const outputs = current.outputs.map((output) => {
          if (output.nodeId !== nodeId) {
            return output;
          }
          const previous = { examples: output.examples ?? [], uploads: output.uploads ?? [] };
          const next = update(previous);
          // Compared by entry, not by array: an updater that rebuilds the same list writes nothing.
          if (sameEntries(next.examples, previous.examples) && sameEntries(next.uploads, previous.uploads)) {
            return output;
          }
          const { examples: _examples, uploads: _uploads, ...rest } = output;
          const written: ToolConfigOutput = {
            ...rest,
            ...(next.examples.length > 0 ? { examples: next.examples } : {}),
            ...(next.uploads.length > 0 ? { uploads: next.uploads } : {}),
          };
          changed = true;
          return written;
        });
        return changed ? { ...current, outputs } : current;
      });
    },
    [save, workflowApp],
  );

  const reorderOutputs = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!workflowApp) {
        return;
      }
      // The drag's indices are the drawer's; the stored list can also hold rows whose nodes are gone.
      const canvasIds = workflowApp.getCanvasNodeIds?.();
      const listed = [...workflowApp.draftOverrides.outputs]
        .sort((a, b) => a.order - b.order)
        .filter((output) => canvasIds === undefined || canvasIds.has(output.nodeId));
      const movedId = listed[fromIndex]?.nodeId;
      const targetId = listed[toIndex]?.nodeId;
      if (movedId === undefined || targetId === undefined) {
        return;
      }
      const base = toToolConfig(workflowApp.draftOverrides);
      void save(workflowApp.workflowId, base, (current) => {
        const storedIds = current.outputs.map((output) => output.nodeId);
        return reorderToolConfigOutputs(current, storedIds.indexOf(movedId), storedIds.indexOf(targetId));
      });
    },
    [save, workflowApp],
  );

  return { setAsOutput, removeOutput, setOutputExamples, reorderOutputs, isUpdatingOutput: isSaving };
}

/**
 * The tool's own name, description and thumbnail. Written through the same optimistic writer as
 * inputs and outputs, so a metadata edit serialises against them instead of racing.
 */
export function useAppMetadataMutations(workflowApp: WorkflowApp | null | undefined) {
  const { save } = useToolConfigWriter(workflowApp);

  const patchMetadata = useCallback(
    (patch: Partial<ToolConfigMetadata>) => {
      if (!workflowApp) {
        return;
      }
      const base = toToolConfig(workflowApp.draftOverrides);
      void save(workflowApp.workflowId, base, (current) => ({
        ...current,
        metadata: { ...current.metadata, ...patch },
      }));
    },
    [save, workflowApp],
  );

  return { patchMetadata };
}

export function useAppInputMutations(workflowApp: WorkflowApp | null | undefined) {
  const { save, isSaving } = useToolConfigWriter(workflowApp);

  const patchInput = useCallback(
    (
      nodeId: NodeId,
      patch: Partial<Pick<ToolConfigInput, 'exposed' | 'required' | 'description' | 'defaultValue'>>,
    ) => {
      if (!workflowApp) {
        return Promise.resolve(true);
      }
      const base = toToolConfig(workflowApp.draftOverrides);
      return save(workflowApp.workflowId, base, (current) => ({
        ...current,
        inputs: current.inputs.map((input) => {
          if (input.nodeId !== nodeId) {
            return input;
          }
          const next = { ...input, ...patch };
          if ('required' in patch && patch.required === undefined) {
            delete next.required;
          }
          if ('defaultValue' in patch && patch.defaultValue === undefined) {
            delete next.defaultValue;
          }
          return next;
        }),
      }));
    },
    [save, workflowApp],
  );

  const setInputExposed = useCallback(
    (nodeId: NodeId, exposed: boolean) => {
      if (!workflowApp) {
        return;
      }
      const base = toToolConfig(workflowApp.draftOverrides);
      // Exposing a node that was locked at conversion takes it out of the snapshot, or its lock would keep
      // hiding it. Released on its own, so the release outlives this write failing. Hiding it again later
      // is an ordinary exposure choice.
      if (exposed && useWorkflowStore.getState().recipe.toolConfig?.lockedAtConversion?.includes(nodeId)) {
        releaseFromToolLockSnapshot(workflowApp.workflowId, nodeId);
      }
      void save(workflowApp.workflowId, base, (current) => ({
        ...current,
        inputs: current.inputs.map((input) => (input.nodeId === nodeId ? { ...input, exposed } : input)),
      }));
    },
    [save, workflowApp],
  );

  const setInputDescription = useCallback(
    (nodeId: NodeId, description: string) => patchInput(nodeId, { description }),
    [patchInput],
  );

  const setInputRequired = useCallback(
    (nodeId: NodeId, required: boolean) => patchInput(nodeId, { required: required ? true : undefined }),
    [patchInput],
  );

  const setInputDefaultValue = useCallback(
    (nodeId: NodeId, defaultValue: ToolConfigInput['defaultValue']) => patchInput(nodeId, { defaultValue }),
    [patchInput],
  );

  const reorderInputs = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!workflowApp) {
        return;
      }
      // The drag's indices are the drawer's; the stored list can also hold rows the graph does not list.
      const listed = [...workflowApp.draftOverrides.inputs].sort((a, b) => a.order - b.order);
      const movedId = listed[fromIndex]?.nodeId;
      const targetId = listed[toIndex]?.nodeId;
      if (movedId === undefined || targetId === undefined) {
        return;
      }
      const base = toToolConfig(workflowApp.draftOverrides);
      void save(workflowApp.workflowId, base, (current) => {
        const storedIds = current.inputs.map((input) => input.nodeId);
        return reorderToolConfigInputs(current, storedIds.indexOf(movedId), storedIds.indexOf(targetId));
      });
    },
    [save, workflowApp],
  );

  return {
    setInputExposed,
    setInputDescription,
    setInputRequired,
    setInputDefaultValue,
    reorderInputs,
    isUpdatingInput: isSaving,
  };
}
